import {
  useMutation,
  useQuery,
  useQueries,
  queryOptions,
} from "@tanstack/react-query";
import { useMemo } from "react";

import {
  uploadFireflyImage,
  generateImage,
  pollStatus,
} from "@app/services/adobeFirefly";

const getFireflyStatus = async (params, options = {}) => {
  const response = await pollStatus(params, options);

  if (!response.ok && response.status !== 429) {
    return { status: "failed", ...response.data };
  }

  const data = response.data ?? {};
  const hasOutputs =
    data?.outputs?.length > 0 || data?.result?.outputs?.length > 0;

  return {
    status: hasOutputs ? "succeeded" : "in_progress",
    ...data,
  };
};

const useFireflyStatus = (
  options = {},
  { onStatus, getStatusFn = getFireflyStatus } = {},
) => {
  return useMutation({
    mutationFn: async (variables, { signal }) => {
      const { statusUrl, shouldSave, modelVersion } = variables;
      const statusResponse = await getStatusFn(
        { statusUrl, shouldSave, modelVersion },
        { signal: variables.signal ?? signal },
      );
      const status = statusResponse?.status;

      if (status === "succeeded") {
        return statusResponse;
      } else if (status === "failed") {
        throw new Error("Render job failed", { cause: statusResponse });
      }

      onStatus?.(statusResponse);

      throw new Error("Firefly image generation in progress", {
        cause: statusResponse,
      });
    },
    retryDelay: 5000,
    retry: (failureCount, error) => {
      if (error?.message?.toLowerCase()?.includes("failed")) {
        return false;
      }
      return true;
    },
    meta: {
      errorMessage: "Failed to fetch Firefly job status. Please try again.",
    },
    ...options,
  });
};

/**
 * Maximum number of reference images the Google (`nano-banana*`) models accept
 * in a single generation request.
 */
export const MAX_REFERENCE_BLOBS = 6;

/** Models that accept more than one reference image. */
export const supportsMultipleReferences = (model) =>
  typeof model === "string" && model.startsWith("nano-banana");

const buildModelParams = (model, options = {}) => {
  const { prompt, negativePrompt } = options;

  // Callers can pin the seed — e.g. to keep a variation visually consistent
  // with the reference image it is derived from. Otherwise pick a random one.
  const seed =
    /* typeof options.seed === "number"
      ? options.seed
      : */ Math.floor(Math.random() * (999999 - 100000 + 1)) + 100000;

  switch (model) {
    case "veo":
      const veoParams = {
        seeds: [seed],
        output: { storeInputs: false },
        locale: "en-US",
        size: [
          {
            width: 1920,
            height: 1080,
          },
        ],
        modelId: model,
        modelVersion: "3.1-generate",
        prompt,
        duration: 5,
        generateAudio: false,
        generationMetadata: { module: "text2video" },
      };

      /**
       * "negativePrompt": "cartoon, vector art, & bad aesthetics & poor aesthetic"
       */
      if (options.negativePrompt) {
        videoParams.negativePrompt = options.negativePrompt;
      }

      if (options.uploadId) {
        ret.referenceBlobs = [
          { id: options.uploadId, usage: "general", promptReference: 1 },
        ];
      }

      if (options.duration) {
        veoParams.duration = options.duration;
      }

      return veoParams;
    case "video":
      const videoParams = {
        n: 1,
        seeds: [seed],
        output: { storeInputs: false },
        locale: "en-US",
        sizes: [
          {
            width: 1920,
            height: 1080,
            numFrames: 128,
          },
        ],
        modelVersion: "video1_8_standard",
        videoSettings: {
          cameraMotion: "camera pan left",
          shotAngle: "eye_level shot",
          shotSize: "close-up shot",
        },
        prompt,
        caiClaimVersion: 2,
        modelSpecificPayload: {
          caiClaimVersion: 2,
        },
      };

      if (options.uploadId) {
        videoParams.image = { source: { id: options.uploadId } };
      }

      if (options.numFrames) {
        videoParams.sizes[0].numFrames = options.numFrames;
      }

      /**
       * "conditions": [
            {
              "source": { "id": "2cdb8aa4-2217-4d65-845a-fb5d5219c98c" },  // first frame
              "placement": { "start": 0 }
            },
            {
              "source": { "id": "b3dd9b94-1b7b-41b4-8cb9-3d06785aff5b" },  // last frame
              "placement": { "start": 1 }
            }
          ]
       */
      if (options.imageConditions) {
        videoParams.image = { conditions: options.imageConditions };
      }

      /** Note: For image‑to‑video (I2V), only cameraMotion is honored; shotAngle and shotSize are inferred from the reference image.
       * cameraMotion
       * "camera pan left"
       * "camera pan right"
       * "camera zoom in"
       * "camera zoom out"
       * "camera tilt up"
       * "camera tilt down"
       * "camera locked down"
       * "camera handheld"
       * shotAngle
       * "aerial shot"
       * "eye_level shot"
       * "high angle shot"
       * "low angle shot"
       * "top-down shot"
       * "none of the above"
       * shotSize
       * "close-up shot"
       * "extreme close-up shot"
       * "medium shot"
       * "long shot"
       * "extreme long shot"
       * "none of the above"
       */
      if (options.videoSettings) {
        videoParams.videoSettings = {
          ...videoParams.videoSettings,
          ...options.videoSettings,
        };
      }

      return videoParams;
    case "nano-banana":
    case "nano-banana-2":
    case "nano-banana-3":
      const nanoBananaParams = {
        n: 1,
        seeds: [seed],
        output: {
          storeInputs: true,
          cai: { directive: "sign_for_policy_mandate" },
        },
        prompt,
        modelId: "gemini-flash",
        modelVersion: model,
        generationMetadata: {
          module: "text2image",
          submodule: "ff-image-generate",
        },
        modelSpecificPayload: {
          parameters: { addWatermark: false },
          aspectRatio: options.aspectRatio,
          // temperature: options.temperature ?? 0,
          // top_p: options.top_p ?? 0.95,
          // top_k: options.top_k ?? 30,
          // max_output_tokens: options.max_output_tokens ?? 8192,
          // seed,
        },
        groundSearch: true,
        caiClaimVersion: 2,
      };

      if (options.aspectRatio) {
        if (
          ["1:1", "4:3", "3:4", "16:9", "9:16", "21:9", "auto"].includes(
            options.aspectRatio,
          )
        ) {
          nanoBananaParams.modelSpecificPayload.aspectRatio =
            options.aspectRatio;
        } else {
          console.warn(
            `Invalid aspect ratio "${options.aspectRatio}" provided. Skipping aspect ratio setting.`,
          );
        }
      }

      if (options.size) {
        if ([512, 1024, 2048, 4096].includes(options.size)) {
          nanoBananaParams.size = {
            width: options.size,
            height: options.size,
          };
        } else if (options.size?.width && options.size?.height) {
          nanoBananaParams.size = {
            width: options.size.width,
            height: options.size.height,
          };
        } else {
          console.warn(
            `Invalid size "${options.size}" provided. Skipping size setting.`,
          );
        }
      }

      if (Array.isArray(options.uploadIds) && options.uploadIds.length) {
        const uploadIds = Array.from(
          new Set(options.uploadIds.filter(Boolean)),
        );

        if (uploadIds.length > MAX_REFERENCE_BLOBS) {
          console.warn(
            `Received ${uploadIds.length} image references, only the first ${MAX_REFERENCE_BLOBS} are sent to "${model}".`,
          );
        }

        nanoBananaParams.referenceBlobs = uploadIds
          .slice(0, MAX_REFERENCE_BLOBS)
          .map((id) => ({ id, usage: "general" }));
      } else if (options.uploadId) {
        nanoBananaParams.referenceBlobs = [
          { id: options.uploadId, usage: "general" },
        ];
      }

      if (options.groundSearch) {
        nanoBananaParams.groundSearch = options.groundSearch;
      }

      return nanoBananaParams;
    case "image5":
      const ret = {
        seeds: [seed],
        output: {
          cai: { directive: "sign_for_policy_mandate" },
          storeInputs: true,
        },
        prompt,
        modelId: "firefly_image",
        modelVersion: "image5",
        generationMetadata: { module: "text2image" },
        caiClaimVersion: 2,
        modelSpecificPayload: {
          caiClaimVersion: 2,
        },
      };

      if (options.uploadId) {
        ret.referenceBlobs = [{ id: options.uploadId, usage: "general" }];
      }

      if (options.aspectRatio && !options.uploadId) {
        if (
          ["1:1", "4:3", "3:4", "16:9", "9:16", "auto"].includes(
            options.aspectRatio,
          )
        ) {
          ret.aspectRatio = options.aspectRatio;
        } else {
          console.warn(
            `Invalid aspect ratio "${options.aspectRatio}" provided. Skipping aspect ratio setting.`,
          );
        }
      }

      if (options.resolutionLevel) {
        if (["1MP", "2.4MP", "4MP"].includes(options.resolutionLevel)) {
          ret.resolutionLevel = options.resolutionLevel;
        } else {
          console.warn(
            `Invalid resolution level "${options.resolutionLevel}" provided. Skipping resolution level setting.`,
          );
        }
      }

      if (options.promptReasoner) {
        if (["speed", "quality", "budget"].includes(options.promptReasoner)) {
          ret.modelSpecificPayload.promptReasoner = options.promptReasoner;
        } else {
          console.warn(
            `Invalid prompt reasoner "${options.promptReasoner}" provided. Skipping prompt reasoner setting.`,
          );
        }
      }

      if (options.size) {
        const { width, height } = options.size;
        if (
          typeof width === "number" &&
          typeof height === "number" &&
          width > 0 &&
          height > 0
        ) {
          ret.size = { width, height };
        } else {
          console.warn(
            `Invalid size provided. Width and height should be positive numbers. Skipping size setting.`,
          );
        }
      }

      if (negativePrompt) {
        ret.negativePrompt = negativePrompt;
      }

      return ret;
    case "image4_ultra":
    default:
      const ff4Params = {
        model: "image4_ultra",
        prompt,
        promptEnhance: "on",
        contentClass: "photo",
        detailLevel: "full",
        visualIntensity: 8,
        caiClaimVersion: 2,
      };

      if (negativePrompt) {
        ff4Params.negativePrompt = negativePrompt;
      }

      if (options.aspectRatio) {
        switch (options.aspectRatio) {
          case "1:1":
            ff4Params.size = { width: 2048, height: 2048 };
            break;
          case "4:3":
            ff4Params.size = { width: 2304, height: 1792 };
            break;
          case "3:4":
            ff4Params.size = { width: 1792, height: 2304 };
            break;
          case "9:16":
            ff4Params.size = { width: 1536, height: 2688 };
            break;
          case "16:9":
          default:
            ff4Params.size = { width: 2688, height: 1536 };
            break;
        }
      } else {
        ff4Params.size = { width: 2688, height: 1536 };
      }

      if (options.uploadId) {
        ff4Params.style = {
          strength: 50,
          imageReference: {
            source: {
              uploadId: options.uploadId,
            },
          },
        };
      }

      return ff4Params;
  }
};

export const fireflyKey = {
  all: ["firefly"],
  imageReferences: () => [...fireflyKey.all, "imageReference"],
  imageReference: (key) => [...fireflyKey.imageReferences(), key],
  images: () => [...fireflyKey.all, "image"],
  image: (model, imageReferenceId, prompt) => [
    ...fireflyKey.images(),
    model,
    imageReferenceId,
    prompt,
  ],
};

export const imageReferenceQueryOptions = (
  sourceReference,
  sourceReferenceKey,
  options = {},
) =>
  queryOptions({
    queryKey: fireflyKey.imageReference(sourceReferenceKey ?? sourceReference),
    queryFn: async ({ signal }) =>
      uploadFireflyImage(sourceReference, { signal }),
    enabled: !!sourceReference,
    staleTime: 24 * 60 * 60 * 1000, // 24 hours
    ...options,
  });

export const generateImageFn = async (
  options = {},
  signal,
  statusCallback = null,
) => {
  const { model, s3FilePath, ...modelOptions } = options;

  // Rate limiting/queueing is handled per Firefly endpoint inside
  // `generateImage`, so requests over the limit wait for a free slot (and for
  // any `Retry-After` window) instead of failing fast.
  return generateImage(
    {
      ...buildModelParams(model, modelOptions),
      ...(s3FilePath ? { s3FilePath } : {}),
    },
    { signal },
    statusCallback,
  );
};

export const fireflyImageQueryOptions = (
  model,
  imageReferenceId,
  prompt,
  options = {},
) =>
  queryOptions({
    queryKey: fireflyKey.image(model, imageReferenceId, prompt),
    queryFn: ({ signal }) =>
      generateImageFn({ model, uploadId: imageReferenceId, prompt }, signal),
    enabled: !!imageReferenceId && !!prompt,
    staleTime: 60 * 60 * 1000, // 1 hour
    ...options,
  });

export const useImageReference = (
  sourceReference,
  sourceReferenceKey,
  options = {},
) => {
  return useQuery(
    imageReferenceQueryOptions(sourceReference, sourceReferenceKey, options),
  );
};

/**
 * Upload several reference images to Firefly at once, reusing the same cache
 * entries as `useImageReference`.
 *
 * @param {Array<{key: string, url: string, cacheKey?: string}>} sources - Reference images to upload.
 * @returns {{ byKey: Object, isFetching: boolean, isError: boolean, error: any }}
 *   `byKey` maps each source `key` to its Firefly upload id (or `null`).
 */
export const useImageReferences = (sources, options = {}) => {
  const list = useMemo(
    () => (sources ?? []).filter((source) => !!source?.key && !!source?.url),
    [sources],
  );

  return useQueries({
    queries: list.map((source) =>
      imageReferenceQueryOptions(
        source.url,
        source.cacheKey ?? source.key,
        options,
      ),
    ),
    combine: (results) => ({
      byKey: Object.fromEntries(
        results.map((result, idx) => [list[idx].key, result.data?.id ?? null]),
      ),
      isFetching: results.some((result) => result.isFetching),
      isError: results.some((result) => result.isError),
      error: results.find((result) => result.isError)?.error ?? null,
    }),
  });
};

export const useFireflyImage = (
  model,
  imageReferenceId,
  prompt,
  options = {},
) => {
  return useQuery(
    fireflyImageQueryOptions(model, imageReferenceId, prompt, options),
  );
};

export { useFireflyStatus };
